import { SERVICE_LINKS } from "@/lib/content";

const BASE = (process.env.REACT_APP_SITE_URL || "https://prox-au.com").replace(/\/$/, "");

const DEFAULT_TITLE = "Prox · Product Sourcing, Costing & Fashion Buying Advisory · Australia";
const DEFAULT_DESC =
  "Prox is a Melbourne product sourcing, procurement, costing, pricing and fashion buying & merchandising advisory for Australian and New Zealand brands. Source better, price for margin, and launch with confidence.";
const DEFAULT_KEYWORDS =
  "product sourcing Australia, fashion sourcing, supplier and factory sourcing, procurement, supplier negotiation, product costing, garment costing, landed cost, pricing strategy, gross margin, fashion buying, fashion merchandising, range planning, open to buy, sourcing consultant Melbourne, sourcing consultant Australia";
const DEFAULT_OG = `${BASE}/og-card.jpg`;

export function breadcrumbJsonLd(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      ...(c.to ? { item: `${BASE}${c.to}` } : {}),
    })),
  };
}

// React 19 hoists title, meta and link into <head>.
export default function Seo({ title, description, canonical, type = "website", jsonLd, keywords, crumbs, image }) {
  const url = canonical ? `${BASE}${canonical}` : BASE;
  const fullTitle = title ? `${title} · Prox` : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const kw = keywords || DEFAULT_KEYWORDS;
  const ogImage = image || DEFAULT_OG;
  const blocks = Array.isArray(jsonLd) ? [...jsonLd] : jsonLd ? [jsonLd] : [];
  if (crumbs && crumbs.length) blocks.push(breadcrumbJsonLd(crumbs));
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Prox" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      {blocks.map((b, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(b)}
        </script>
      ))}
    </>
  );
}

const SERVICE_OFFERS = SERVICE_LINKS.map((s) => ({
  "@type": "Offer",
  itemOffered: { "@type": "Service", name: s.title, url: `${BASE}/services/${s.slug}` },
}));

// Rich entity graph used on the homepage: who Prox is, where it operates, what it knows and offers.
export const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": `${BASE}/#org`,
      name: "Prox",
      alternateName: "Prox Advisory",
      url: BASE,
      email: "info@prox-au.com",
      description:
        "Melbourne-based product sourcing, procurement, costing, pricing and fashion buying & merchandising advisory for Australian and New Zealand brands.",
      slogan: "Source better. Price smarter. Launch with confidence.",
      areaServed: [
        { "@type": "Country", name: "Australia" },
        { "@type": "Country", name: "New Zealand" },
      ],
      address: {
        "@type": "PostalAddress",
        addressLocality: "South Melbourne",
        addressRegion: "VIC",
        postalCode: "3205",
        addressCountry: "AU",
      },
      geo: { "@type": "GeoCoordinates", latitude: -37.8329, longitude: 144.9615 },
      founder: {
        "@type": "Person",
        name: "Rasika Khode",
        jobTitle: "Founder & Principal",
        sameAs: ["https://www.linkedin.com/in/rasikakhode/"],
      },
      sameAs: ["https://www.linkedin.com/in/rasikakhode/"],
      priceRange: "$$",
      knowsLanguage: ["en"],
      knowsAbout: [
        "Product sourcing", "Strategic sourcing", "Procurement", "Supplier and factory sourcing",
        "Supplier negotiation", "Fashion sourcing", "Wholesale sourcing", "Private label manufacturing",
        "Product costing", "Garment costing", "Apparel costing", "Landed cost calculation",
        "Pricing strategy", "Gross margin", "Margin analysis", "Intake margin",
        "Fashion buying", "Fashion merchandising", "Range planning", "Assortment planning",
        "Open to buy", "Buying budget", "Merchandise planning", "Inventory planning", "Markdown planning",
        "Sell-through analysis", "Accessories sourcing", "Jewellery sourcing", "Footwear sourcing",
        "Product development", "First production run",
      ],
      serviceType: [
        "Product sourcing", "Procurement", "Product costing & pricing", "Fashion buying & merchandising",
        "Range & buy planning", "Product development & quality", "Inventory & markdown planning",
        "New fashion brand launch planning",
      ],
      makesOffer: SERVICE_OFFERS,
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Commercial advisory services",
        itemListElement: SERVICE_OFFERS,
      },
    },
    {
      "@type": "WebSite",
      "@id": `${BASE}/#website`,
      url: BASE,
      name: "Prox",
      inLanguage: "en-AU",
      publisher: { "@id": `${BASE}/#org` },
    },
  ],
};
